import {
  Card,
  Empty,
  Form,
  Input,
  Space,
  Tooltip,
  Typography,
  AutoComplete,
} from 'antd';
import { useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { RevxButton } from 'components/RevxButton';
import { useOrgId } from 'hooks/useOrg';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import { EditOutlined } from '@ant-design/icons';
import shippingService from 'services/shippingService';
import states from 'states-us';
import { isUnitedStates } from 'utils/address';
import AddressValidation, {
  ValidationRef,
} from './components/AddressValidation';
import { OrgShippingAddress } from 'domains/orgDomain';

const ShippingAddressForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const shippingConfig = useAppSelector(
    (state) => state.shipping.orgShippingConfig
  );
  const shippingAddress = shippingConfig?.shipFrom;

  const orgId = useOrgId();

  const [editing, setEditing] = useState<boolean>(false);
  const addrValidationRef = useRef<ValidationRef>(null);

  const doSave = async (values: OrgShippingAddress) => {
    await shippingService.updateOrgShippingConfigAddress(orgId, values);
    addrValidationRef.current?.reset();
    setEditing(false);
  };

  const onSave = (validateAddress?: boolean) => {
    form.validateFields().then(async (values: OrgShippingAddress) => {
      if (validateAddress) {
        const ret = await addrValidationRef.current?.validate({
          addressLine: values.addressLine1,
          city: values.city,
          stateProvinceCode: values.stateProvinceCode,
          countryCode: values.countryCode,
          postalCode: values.postalCode,
        });
        if (ret) {
          await doSave(values);
        }
      }
    });
  };

  const openProp = editing ? {} : { open: false };
  const [countryCode, setCountryCode] = useState(shippingAddress?.countryCode);
  const allStatesOptions = states.map((s) => {
    return {
      value: s.abbreviation,
      label: `${s.abbreviation} (${s.name})`,
    };
  });
  const [stateOptions, setStateOptions] = useState(allStatesOptions);

  const onSearchStates = (searchText: string) => {
    if (isUnitedStates(countryCode)) {
      if (!searchText) {
        setStateOptions(allStatesOptions);
      } else {
        setStateOptions(
          allStatesOptions.filter((o) => {
            if (searchText.length === 1) {
              return o.value.toLowerCase().startsWith(searchText.toLowerCase());
            } else if (searchText.length === 2) {
              return o.value.toLowerCase() === searchText.toLowerCase();
            }
            return o.label.toLowerCase().includes(searchText.toLowerCase());
          })
        );
      }
    }
  };

  return (
    <Card
      title={t('platform.shipping.shippingAddress')}
      headStyle={{ height: 60 }}
      style={{ width: 600 }}
      className="shipping-card"
      extra={
        <Space>
          {!editing && shippingAddress && (
            <Tooltip title={t('common.edit')}>
              <EditOutlined
                onClick={() => {
                  setEditing(true);
                }}
              />
            </Tooltip>
          )}

          {editing && (
            <Tooltip title="Save with address validation">
              <Typography.Link
                onClick={() => {
                  onSave(true);
                }}
              >
                {t('common.save')}
              </Typography.Link>
            </Tooltip>
          )}
        </Space>
      }
    >
      {shippingAddress ? (
        <Form
          labelCol={{ span: 8 }}
          form={form}
          wrapperCol={{ span: 16 }}
          layout={'horizontal'}
          initialValues={{
            ...shippingAddress,
          }}
          autoComplete="off"
        >
          <AddressValidation
            address={{
              addressLine: form.getFieldValue('addressLine1'),
              city: form.getFieldValue('city'),
              stateProvinceCode: form.getFieldValue('state'),
              countryCode: form.getFieldValue('country'),
              postalCode: form.getFieldValue('postalCode'),
            }}
            onApply={(record) => {
              form.setFieldsValue({
                addressLine1: record.addressLine,
                city: record.city,
                state: record.stateProvinceCode,
                country: record.countryCode,
                postalCode: record.postalCode,
              });
            }}
            ref={addrValidationRef}
          />

          <Form.Item
            label={t('platform.shipping.form.phoneNumber')}
            name="phoneNumber"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            label={t('platform.shipping.form.countryCode')}
            name="countryCode"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <AutoComplete
              onChange={(val) => {
                setCountryCode(val);
                if (isUnitedStates(val)) {
                  setStateOptions(allStatesOptions);
                } else {
                  setStateOptions([]);
                }
              }}
              options={[
                { label: 'US', value: 'US' },
                { label: 'CA', value: 'CA' },
              ]}
              {...openProp}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            ></AutoComplete>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.countryCode !== cur.countryCode}
          >
            {({ getFieldValue }) => (
              <Form.Item
                label={t('platform.shipping.form.stateProvinceCode')}
                name="stateProvinceCode"
                // only US addresses need a state; most countries have none (PRD-746).
                // `dependencies` re-validates when the country changes, and the rule reads
                // it as it validates, so the error clears instead of sticking.
                dependencies={['countryCode']}
                required={isUnitedStates(getFieldValue('countryCode'))}
                rules={[
                  ({ getFieldValue: getCurrent }) => ({
                    required: isUnitedStates(getCurrent('countryCode')),
                  }),
                ]}
              >
                {/* the suggestions are US state codes, so offer them only for US
                    addresses; elsewhere a province is free text (PRD-746) */}
                {isUnitedStates(getFieldValue('countryCode')) ? (
                  <AutoComplete
                    {...openProp}
                    onSearch={onSearchStates}
                    options={stateOptions}
                    className={classNames({
                      'revx-input-no-border': !editing,
                    })}
                  ></AutoComplete>
                ) : (
                  <Input
                    readOnly={!editing}
                    autoComplete="off"
                    className={classNames({
                      'revx-input-no-border': !editing,
                    })}
                  />
                )}
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.city')}
            name="city"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.addressLine1')}
            name="addressLine1"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.addressLine2')}
            name="addressLine2"
            rules={[RULE_TEXT_LEN_255, { required: false }]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>

          <Form.Item
            label={t('platform.shipping.form.postalCode')}
            name="postalCode"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
        </Form>
      ) : (
        <Empty
          description={
            <span>
              {t('platform.shipping.shippingAddressEmptyDesc')}
              <RevxButton
                type="link"
                size="small"
                onClick={() => setEditing(true)}
              >
                {t('common.create')}
              </RevxButton>
            </span>
          }
        />
      )}
    </Card>
  );
};

export default ShippingAddressForm;
